// ============================================================
// GOOGLE DATA CONTEXT — ログイン時ブートストラップ同期＋Google予定の共有ストア。
//
// 従来は Calendar.tsx がマウント時に fetchGoogleCalendarEvents、MyTasks が
// マウント時に syncMyTasksWithGoogle を各自実行していた＝「その画面を開くまで
// Google データが来ない」。本 Provider は AuthGate の内側（ログイン後のみ）に置かれ、
// googleAccessToken が確定した時点で1回だけ：
//   a) タスク双方向同期 syncMyTasksWithGoogle（taskSync.ts。Firestore syncLock で
//      サーバー同期・MyTasks 側の runSync と排他。失敗は握りつぶし＝UIを壊さない）
//   b) fetchGoogleCalendarEvents（defaultCalendarRange＝今月前後）で Google 予定を
//      取得し context state に保持 → Home のミニカレンダー / Calendar 画面が共有消費。
//
// MyTasks の既存 runSync はそのまま残す（30秒スロットル＋syncingRef＋Firestore
// syncLock で二重実行は防がれる。ここでは変更しない）。
// トークン無し（DEV / 失効 / ログアウト）は googleEvents=[] に倒す（未接続相当）。
// エラーは throw せず console.warn ＋ 空配列（google.ts / taskSync.ts と同じ方針）。
// ============================================================
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { getAdapter } from '../data/adapter';
import { defaultCalendarRange, fetchGoogleCalendarEvents } from './google';
import { syncMyTasksWithGoogle } from './taskSync';
import type { SynthesizedCalendarEntry } from '../types/models';

export interface GoogleDataState {
  /** Google カレンダー予定（source='google'・今月前後レンジ）。未接続・失敗時は []。 */
  googleEvents: SynthesizedCalendarEntry[];
  /** Google 予定の取得中フラグ（Calendar 画面の「読み込み中…」表示用）。 */
  googleEventsLoading: boolean;
  /** Google 予定を再取得する（手動リフレッシュ導線用）。未接続時は [] に倒すだけ。 */
  refreshGoogleEvents: () => Promise<void>;
}

const GoogleDataContext = createContext<GoogleDataState | null>(null);

export function GoogleDataProvider({ children }: { children: ReactNode }) {
  // viewer は AuthGate 内側なら非null だが、型上 null を許すので optional 参照に留める。
  const { googleAccessToken, viewer, uid } = useAuth();
  const [googleEvents, setGoogleEvents] = useState<SynthesizedCalendarEntry[]>([]);
  const [googleEventsLoading, setGoogleEventsLoading] = useState(false);
  // 同一トークンでのブートストラップは1回だけ（再レンダー・画面遷移で再発火させない）。
  // 再ログインでトークンが変われば改めて1回走る。
  const bootstrappedTokenRef = useRef<string | null>(null);
  // タスク同期の多重起動ガード（MyTasks の syncingRef と同じ流儀・Provider 内ローカル）。
  const syncingRef = useRef(false);

  const refreshGoogleEvents = useCallback(async () => {
    if (!googleAccessToken) {
      // 未接続（DEV/失効/ログアウト）→ 空に倒す（Calendar の従来挙動と同じ）。
      setGoogleEvents([]);
      setGoogleEventsLoading(false);
      return;
    }
    setGoogleEventsLoading(true);
    try {
      // fetch 側（google.ts）が 401/例外を握りつぶして [] を返すが、念のため二重ガード。
      const evs = await fetchGoogleCalendarEvents(googleAccessToken, defaultCalendarRange());
      setGoogleEvents(evs);
    } catch (err) {
      console.warn('[GoogleData] Google予定の取得で例外', err instanceof Error ? err.message : err);
      setGoogleEvents([]);
    } finally {
      setGoogleEventsLoading(false);
    }
  }, [googleAccessToken]);

  // トークン確定時に1回だけブートストラップ：a) タスク同期 b) Google予定取得。
  useEffect(() => {
    if (!googleAccessToken) {
      // トークンが無くなった（ログアウト/失効後の再マウント）→ 表示も未接続相当へ。
      bootstrappedTokenRef.current = null;
      setGoogleEvents([]);
      setGoogleEventsLoading(false);
      return;
    }
    if (bootstrappedTokenRef.current === googleAccessToken) return; // 同一トークンは1回だけ
    bootstrappedTokenRef.current = googleAccessToken;

    // b) Google予定の取得（Home ミニカレンダーを早く出すため同期を待たず並行）。
    void refreshGoogleEvents();

    // a) タスク双方向同期（MyTasks の runSync 実装を移植：多重起動ガード＋例外握りつぶし。
    //    30秒スロットルは持たない＝トークン確定ごとに1回きりなので不要。Firestore の
    //    syncLock（uid キー）が MyTasks/サーバー同期との同時実行を防ぐ）。
    const viewerId = viewer?.id;
    if (!viewerId) return; // viewer 未確定（理論上 AuthGate 内では起きない）→ 同期はスキップ
    if (syncingRef.current) return;
    syncingRef.current = true;
    void (async () => {
      try {
        const adapter = await getAdapter();
        // 最新のタスク（自分担当の起点）を取得してから同期（MyTasks と同じ）。
        const current = await adapter.getTasks();
        await syncMyTasksWithGoogle({
          token: googleAccessToken,
          viewerId,
          uid, // ユーザー単位の同期ロック（syncLocks/{uid}）のキー。
          appTasks: current,
          updateTask: (id, patch) => adapter.updateTask(id, patch),
          createTask: (input) => adapter.createTask(input),
        });
        // 画面への反映は各画面の subscribeTasks（リアルタイム購読）が拾う。
        // ここで setState はしない（Provider はタスク一覧を持たない）。
      } catch (err) {
        // 同期失敗は UI を壊さない。秘密（トークン等）はログに出さない。
        console.warn('[GoogleData] ブートストラップ同期で例外', err instanceof Error ? err.message : err);
      } finally {
        syncingRef.current = false;
      }
    })();
  }, [googleAccessToken, viewer?.id, uid, refreshGoogleEvents]);

  return (
    <GoogleDataContext.Provider value={{ googleEvents, googleEventsLoading, refreshGoogleEvents }}>
      {children}
    </GoogleDataContext.Provider>
  );
}

/** Google 共有データ（予定・取得状態・再取得）を取得する。Provider の外で呼ぶと throw。 */
export function useGoogleData(): GoogleDataState {
  const ctx = useContext(GoogleDataContext);
  if (!ctx) throw new Error('useGoogleData must be used within <GoogleDataProvider>');
  return ctx;
}
